import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Loader2, ArrowLeft, Pause, Play, Repeat, Upload, Languages, Wand2, Pencil,
} from 'lucide-react';
import { vocabularyApi, videoLibraryApi } from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { statusColor } from '../shared/reader/statusColor';
import { useWordPopover } from '../shared/reader/useWordPopover';
import WordPopover from '../shared/reader/WordPopover';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// --- Load YouTube IFrame API once ---
let ytApiPromise = null;
function loadYouTubeApi() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return ytApiPromise;
}

export default function VideoReader() {
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const lang = searchParams.get('lang') || 'en';
  const { locale } = useI18n();
  // Native language = the user's interface locale (strip region)
  const nativeLang = (locale || 'en').split('-')[0].toLowerCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [autopause, setAutopause] = useState(true);
  const [loopSentence, setLoopSentence] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  // Subtitle-vs-audio synchronism offset (seconds).
  //   positive = subtitles are AHEAD → delay them (shift snippet times forward)
  //   negative = subtitles are BEHIND → play them earlier
  const [syncOffset, setSyncOffset] = useState(0);

  // Popover — powered by the shared useWordPopover hook (same UX as Reader)
  const { user } = useAuth();
  const popover = useWordPopover({
    languageCode: lang,
    interfaceLang: nativeLang,
  });
  const [actionLoading, setActionLoading] = useState(false);

  // Manual subs pasting
  const [manualText, setManualText] = useState('');
  const [uploadingSubs, setUploadingSubs] = useState(false);
  const [whispering, setWhispering] = useState(false);

  // Admin: edit the global subtitle track
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorText, setEditorText] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);

  const playerRef = useRef(null);
  const playerElRef = useRef(null);
  const transcriptRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const fetchVideo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/video-library/${videoId}?lang=${lang}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch (err) {
      toast.error('Could not load video');
    } finally {
      setLoading(false);
    }
  }, [videoId, lang]);

  useEffect(() => { fetchVideo(); }, [fetchVideo]);

  // --- Init YouTube player ---
  useEffect(() => {
    if (!data?.external_id) return;
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled) return;
      if (playerRef.current) { try { playerRef.current.destroy(); } catch {} }
      playerRef.current = new YT.Player(playerElRef.current, {
        videoId: data.external_id,
        playerVars: { playsinline: 1, modestbranding: 1, rel: 0 },
        events: {
          onReady: () => {
            pollRef.current = setInterval(() => tickRef.current && tickRef.current(), 250);
          },
          onStateChange: () => {},
        },
      });
    });
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (playerRef.current) { try { playerRef.current.destroy(); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.external_id]);

  const tick = () => {
    const p = playerRef.current;
    if (!p || !p.getCurrentTime || !data?.snippets?.length) return;
    // Subtract the user's sync offset so that shifting subtitles forward (+)
    // makes them appear LATER relative to the audio.
    const t = p.getCurrentTime() - syncOffset;
    // Find active snippet
    const snippets = data.snippets;
    let idx = -1;
    for (let i = 0; i < snippets.length; i++) {
      const s = snippets[i];
      if (t >= s.start && t < s.start + (s.duration || 2.5)) { idx = i; break; }
      if (s.start > t) { break; }
    }
    if (idx !== activeIdx) {
      if (loopSentence && activeIdx >= 0 && idx > activeIdx) {
        // Loop the previous sentence
        p.seekTo(snippets[activeIdx].start, true);
        return;
      }
      setActiveIdx(idx);
      // Scroll active line into view
      if (idx >= 0) {
        const el = transcriptRef.current?.querySelector(`[data-snippet-idx="${idx}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };
  // Keep tickRef pointing at the latest closure so setInterval sees current state
  tickRef.current = tick;

  const handleWordClick = (word, e) => {
    e?.stopPropagation?.();
    const term = (word || '').trim();
    if (!term) return;
    if (autopause && playerRef.current?.pauseVideo) {
      try { playerRef.current.pauseVideo(); } catch {}
    }
    // Synthesise a token-like object so useWordPopover can open the popover
    // against the clicked DOM node.
    const syntheticToken = {
      token_id: `video-${term}-${Date.now()}`,
      normalized_form: term.toLowerCase(),
      surface_form: term,
      is_word: true,
      status: 'new',
      strength: 0,
    };
    popover.open(syntheticToken, { currentTarget: e.currentTarget });
  };

  const handleSetStatus = async (status) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      await vocabularyApi.setWordStatus(term, lang, status);

      // Approximate strength for the local highlight re-color
      const strengthMap = { ignored: null, new: 0.0, learning: 0.2, familiar: 0.5, known: 0.8, mastered: 1.0 };
      const newStrength = strengthMap[status] ?? 0;

      // Update matching tokens in all snippets locally — DO NOT refetch,
      // otherwise the whole page (incl. YouTube iframe) gets remounted.
      setData((prev) => {
        if (!prev?.snippets) return prev;
        return {
          ...prev,
          snippets: prev.snippets.map((sn) => ({
            ...sn,
            tokens: (sn.tokens || []).map((t) =>
              t.normalized_form === term ? { ...t, status, strength: newStrength } : t
            ),
          })),
        };
      });

      toast.success(`Marked as ${status}`);
      popover.close();
    } catch {
      toast.error('Could not update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveTranslation = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      let stateId = popover.wordData?.user_state?.state_id;
      if (!stateId) {
        // Create state via 'new' status and refetch
        await vocabularyApi.setWordStatus(term, lang, 'new');
        const res = await vocabularyApi.getWordData(term, lang);
        stateId = res.data?.user_state?.state_id;
      }
      if (stateId) {
        await vocabularyApi.addTranslation(stateId, popover.translation);
        popover.setTranslationSaved(true);
        toast.success('Translation saved');
      }
    } catch {
      toast.error('Could not save translation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveNote = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      await vocabularyApi.saveUserNote(term, lang, popover.note);
      toast.success('Note saved');
    } catch {
      toast.error('Could not save note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAutoTranslate = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, lang, 'translation', true);
      const tr = res.data?.data?.translation || res.data?.translation;
      if (tr) {
        popover.setTranslation(tr);
        popover.setTranslationSaved(false);
      }
    } catch {
      toast.error('Translation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshSection = async (section) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, lang, section, true);
      if (res.data?.data) {
        popover.setWordData((prev) => (prev ? { ...prev, ...res.data.data } : res.data.data));
      }
      toast.success(section ? `${section} refreshed` : 'Word enriched');
    } catch {
      toast.error('Refresh failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlayAudio = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const audio = new Audio(
        `${BACKEND_URL}/api/tts/word?word=${encodeURIComponent(term)}&language=${lang}`
      );
      await audio.play();
    } catch {
      toast.error('Audio unavailable');
    }
  };

  const handleSnippetClick = (snippet) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(snippet.start, true);
      playerRef.current.playVideo?.();
    }
  };

  const handleManualSubsUpload = async () => {
    if (!manualText.trim()) return;
    setUploadingSubs(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/video-library/manual-subs`,
        { video_id: data.external_id, language_code: lang, subtitles_text: manualText },
        { withCredentials: true }
      );
      toast.success('Subtitles added!');
      setManualText('');
      await fetchVideo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSubs(false);
    }
  };

  const translationsForNative = data?.translations?.[nativeLang] || null;

  const handleToggleTranslation = async () => {
    if (nativeLang === lang) {
      toast.info("Your interface language matches the video language — nothing to translate.");
      return;
    }
    // Already loaded and matching length → just toggle visibility
    if (translationsForNative && translationsForNative.length === (data?.snippets?.length || 0)) {
      setShowTranslation((s) => !s);
      return;
    }
    // Not yet generated → call the LLM translate endpoint
    setTranslating(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/video-library/${videoId}/translate?lang=${lang}`,
        { native_language_code: nativeLang },
        { withCredentials: true }
      );
      await fetchVideo();
      setShowTranslation(true);
      toast.success('Translation ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const handleWhisperTranscribe = async (chapterIdx = null) => {
    const hasChapters = (data?.chapters || []).length > 0;
    const dur = data?.duration_seconds || 0;
    const isLong = dur > 45 * 60;
    let confirmMsg;
    if (chapterIdx !== null) {
      const ch = data.chapters[chapterIdx];
      confirmMsg = `Transcribe chapter "${ch.title}" with Whisper AI?\n\nThis takes ~1 min per 10 min of audio and uses LLM credits.`;
    } else if (isLong && hasChapters) {
      confirmMsg = `Transcribe this full video (${Math.round(dur / 60)} min, ${data.chapters.length} chapters) with Whisper AI?\n\nChapters will be transcribed in sequence. This can take 5–15 minutes and uses LLM credits.`;
    } else if (isLong) {
      confirmMsg = `Transcribe this long video (${Math.round(dur / 60)} min) with Whisper AI?\n\nThe audio will be auto-split into 30-min chunks. This can take 5–15 minutes and uses LLM credits.`;
    } else {
      confirmMsg = 'Auto-transcribe this video with Whisper AI?\n\nThis takes 1–3 minutes and uses LLM credits.';
    }
    if (!window.confirm(confirmMsg)) return;
    setWhispering(true);
    try {
      const q = chapterIdx !== null ? `&chapter_index=${chapterIdx}` : '';
      const res = await axios.post(
        `${BACKEND_URL}/api/video-library/${videoId}/whisper?lang=${lang}${q}`,
        {},
        { withCredentials: true, timeout: 15 * 60 * 1000 }
      );
      toast.success(`Transcribed ${res.data.snippets} lines (${res.data.windows} chunk${res.data.windows === 1 ? '' : 's'})`);
      await fetchVideo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Whisper transcription failed');
    } finally {
      setWhispering(false);
    }
  };

  const handleChapterSeek = (chapter) => {
    if (playerRef.current?.seekTo) {
      playerRef.current.seekTo(chapter.start_time, true);
      playerRef.current.playVideo?.();
    }
  };

  // ─── Admin: edit global subtitle script ───
  const openScriptEditor = async () => {
    setEditorOpen(true);
    setEditorLoading(true);
    setEditorText('');
    try {
      const res = await videoLibraryApi.adminGetSubtitles(data.video_id, lang);
      setEditorText(res.data?.text || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load subtitles');
      setEditorOpen(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveScriptEditor = async () => {
    if (!editorText.trim()) {
      toast.error('Subtitle text cannot be empty');
      return;
    }
    setEditorSaving(true);
    try {
      const res = await videoLibraryApi.adminReplaceSubtitles(data.video_id, lang, editorText);
      toast.success(`Saved ${res.data?.snippets} lines globally`);
      setEditorOpen(false);
      await fetchVideo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setEditorSaving(false);
    }
  };

  // Which chapter is currently active based on activeIdx's timestamp
  const activeChapter = React.useMemo(() => {
    if (!data?.chapters || data.chapters.length === 0 || activeIdx < 0) return -1;
    const t = data.snippets?.[activeIdx]?.start ?? 0;
    let found = -1;
    data.chapters.forEach((c, i) => {
      if (c.start_time <= t) found = i;
    });
    return found;
  }, [activeIdx, data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading video…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto px-3 py-4" data-testid="video-reader">
      <Button variant="ghost" size="sm" onClick={() => navigate('/videos')} className="mb-3 gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to library
      </Button>

      {/* Title + meta */}
      {data.title && (
        <div className="mb-3">
          <h1 className="text-xl font-semibold leading-tight line-clamp-2">{data.title}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {data.channel && <span>{data.channel}</span>}
            {data.channel && data.category && <span> · </span>}
            {data.category && <span>{data.category}</span>}
            {(data.channel || data.category) && <span> · </span>}
            <span className="uppercase">{data.language_code}</span>
          </p>
        </div>
      )}

      <div className={`grid gap-4 ${(data.chapters || []).length > 0 ? 'grid-cols-1 lg:grid-cols-[1fr_260px]' : 'grid-cols-1'}`}>
        {/* ── Main column ── */}
        <div>
          {/* Player */}
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden mb-4 shadow-xl">
            <div ref={playerElRef} className="w-full h-full" />
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Button
              variant={autopause ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutopause(!autopause)}
              className="gap-1"
              data-testid="autopause-toggle"
            >
              <Pause className="h-4 w-4" /> Autopause
            </Button>
            <Button
              variant={loopSentence ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLoopSentence(!loopSentence)}
              className="gap-1"
              data-testid="loop-toggle"
            >
              <Repeat className="h-4 w-4" /> Loop sentence
            </Button>
            {/* Subtitle sync: nudge captions ±1 s relative to the audio */}
            <div className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs" data-testid="sync-control">
              <span className="text-muted-foreground">Sync</span>
              <button
                type="button"
                onClick={() => setSyncOffset((v) => +(v - 1).toFixed(1))}
                className="px-1.5 py-0.5 rounded hover:bg-muted font-mono"
                title="Subtitles are behind → play them earlier"
                data-testid="sync-minus"
              >
                −1s
              </button>
              <span
                className="font-mono tabular-nums w-12 text-center text-foreground"
                data-testid="sync-offset"
              >
                {syncOffset > 0 ? `+${syncOffset.toFixed(1)}s` : `${syncOffset.toFixed(1)}s`}
              </span>
              <button
                type="button"
                onClick={() => setSyncOffset((v) => +(v + 1).toFixed(1))}
                className="px-1.5 py-0.5 rounded hover:bg-muted font-mono"
                title="Subtitles are ahead → delay them"
                data-testid="sync-plus"
              >
                +1s
              </button>
              {syncOffset !== 0 && (
                <button
                  type="button"
                  onClick={() => setSyncOffset(0)}
                  className="ml-1 text-[10px] text-muted-foreground hover:text-foreground"
                  title="Reset subtitle sync"
                  data-testid="sync-reset"
                >
                  reset
                </button>
              )}
            </div>
            {nativeLang !== lang && !data?.needs_manual_subs && (
              <Button
                variant={showTranslation ? 'default' : 'outline'}
                size="sm"
                onClick={handleToggleTranslation}
                disabled={translating}
                className="gap-1"
                data-testid="translation-toggle"
                title={`Show ${nativeLang.toUpperCase()} translation under each line`}
              >
                {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                {translating ? 'Translating…' : (showTranslation ? `Hide ${nativeLang.toUpperCase()}` : `Show ${nativeLang.toUpperCase()}`)}
              </Button>
            )}
            {/* Admin-only: edit the subtitle script globally (propagates to every user). */}
            {user?.is_admin && !data?.needs_manual_subs && (
              <Button
                variant="outline"
                size="sm"
                onClick={openScriptEditor}
                className="gap-1"
                data-testid="admin-edit-script"
                title="Admin: edit the global subtitle script for this video+language"
              >
                <Pencil className="h-4 w-4" /> Edit script
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              {data.snippets.length} lines · {data.language_code.toUpperCase()} {data.is_generated ? '(auto)' : ''}
            </span>
          </div>

          {/* No subs → Whisper OR manual paste */}
          {data.needs_manual_subs ? (
            <div className="border border-amber-500/40 rounded-lg p-5 bg-amber-500/10">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Upload className="h-4 w-4" /> No captions available
              </h3>
              {data.whisper_job?.status === 'running' ? (
                <div
                  className="flex items-center gap-2 mb-3 text-sm rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-violet-700 dark:text-violet-300"
                  data-testid="auto-whisper-running"
                >
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  <span>
                    Auto-transcription is already running in the background —
                    subtitles will appear here shortly. (Refresh in ~1 minute.)
                  </span>
                </div>
              ) : data.whisper_job?.status === 'failed' ? (
                <div
                  className="mb-3 text-xs rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-red-700 dark:text-red-300"
                  data-testid="auto-whisper-failed"
                >
                  Auto-transcription failed{data.whisper_job.error ? ` (${String(data.whisper_job.error).slice(0, 140)})` : ''}. Try again manually or paste subtitles.
                </div>
              ) : null}
              <p className="text-sm text-muted-foreground mb-3">
                This video doesn't have auto-captions. Auto-transcribe with Whisper AI (works for any length — long videos split into 30-min chunks or by chapter), or paste subtitles manually.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  onClick={() => handleWhisperTranscribe(null)}
                  disabled={whispering || uploadingSubs}
                  className="gap-2"
                  data-testid="whisper-btn"
                >
                  {whispering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {whispering
                    ? 'Transcribing…'
                    : (data.duration_seconds > 45 * 60
                        ? `Auto-transcribe whole video (${Math.round(data.duration_seconds / 60)} min)`
                        : 'Auto-transcribe with Whisper')}
                </Button>
              </div>
              <Textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                rows={8}
                placeholder="line 1&#10;line 2&#10;line 3…"
                className="font-mono text-sm mb-3"
                data-testid="manual-subs-textarea"
              />
              <Button onClick={handleManualSubsUpload} disabled={uploadingSubs || !manualText.trim() || whispering} variant="outline">
                {uploadingSubs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save pasted subtitles
              </Button>
            </div>
          ) : (
            /* Transcript */
            <div
              ref={transcriptRef}
              className="rounded-lg border max-h-[60vh] overflow-y-auto p-3 bg-background"
              data-testid="transcript"
            >
              {data.snippets.map((sn, i) => (
                <div
                  key={i}
                  data-snippet-idx={i}
                  onClick={() => handleSnippetClick(sn)}
                  className={`flex gap-3 px-3 py-2 rounded cursor-pointer transition-all ${
                    i === activeIdx
                      ? 'bg-primary/15 ring-1 ring-primary/40'
                      : i < activeIdx
                        ? 'opacity-40 hover:opacity-70'
                        : 'hover:bg-muted/50'
                  }`}
                  data-testid={`snippet-${i}`}
                >
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0 w-12 pt-1">
                    {Math.floor(sn.start / 60)}:{String(Math.floor(sn.start % 60)).padStart(2, '0')}
                  </span>
                  <div className="text-lg leading-relaxed flex-1">
                    {(sn.tokens || []).map((tok, j) => {
                      if (!tok.is_word) return <span key={j}>{tok.surface_form}</span>;
                      const color = statusColor(tok.status, tok.strength);
                      return (
                        <span
                          key={j}
                          onClick={(e) => { e.stopPropagation(); handleWordClick(tok.normalized_form, e); }}
                          className="cursor-pointer hover:bg-purple-500/25 rounded px-0.5 transition-colors"
                          style={{ color }}
                          data-testid={`video-token-${tok.normalized_form}`}
                        >
                          {tok.surface_form}
                    </span>
                  );
                })}
                {showTranslation && translationsForNative?.[i] && (
                  <div
                    className="text-sm text-muted-foreground italic mt-1 leading-snug"
                    data-testid={`snippet-translation-${i}`}
                  >
                    {translationsForNative[i]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
        </div>
        {/* ── Chapters sidebar ── */}
        {(data.chapters || []).length > 0 && (
          <aside className="rounded-lg border bg-background p-3 max-h-[70vh] overflow-y-auto" data-testid="chapters-sidebar">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              📑 Chapters
              <span className="text-xs text-muted-foreground">({data.chapters.length})</span>
            </h3>
            <ul className="space-y-1">
              {data.chapters.map((c, i) => {
                const isActive = i === activeChapter;
                const mm = Math.floor(c.start_time / 60);
                const ss = String(Math.floor(c.start_time % 60)).padStart(2, '0');
                const end = c.end_time || data.duration_seconds;
                const chapLen = end - c.start_time;
                const chapLenMin = Math.round(chapLen / 60);
                return (
                  <li key={i}>
                    <div
                      className={`group flex items-start gap-2 px-2 py-2 rounded cursor-pointer transition-all ${
                        isActive ? 'bg-primary/15 ring-1 ring-primary/40' : 'hover:bg-muted/60'
                      }`}
                      onClick={() => handleChapterSeek(c)}
                      data-testid={`chapter-${i}`}
                    >
                      <span className="text-[10px] font-mono text-muted-foreground pt-0.5 w-10 shrink-0">{mm}:{ss}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-snug line-clamp-2 ${isActive ? 'text-primary font-medium' : ''}`}>{c.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{chapLenMin} min</p>
                      </div>
                      {data.needs_manual_subs && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleWhisperTranscribe(i); }}
                          disabled={whispering}
                          title="Transcribe this chapter with Whisper"
                          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-1 text-violet-500 hover:text-violet-400 disabled:opacity-30"
                          data-testid={`chapter-whisper-${i}`}
                        >
                          {whispering ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        )}
      </div>

      {/* Shared Word Popover — same UX as the text Reader */}
      {popover.popoverPos && (
        <WordPopover
          style={popover.popoverPos}
          wordData={popover.wordData}
          wordLoading={popover.wordLoading}
          token={popover.selectedToken}
          language={{ code: lang }}
          noteText={popover.note}
          setNoteText={popover.setNote}
          translationText={popover.translation}
          setTranslationText={popover.setTranslation}
          translationSaved={popover.translationSaved}
          onPlayAudio={handlePlayAudio}
          onSetStatus={handleSetStatus}
          onRelatedWordClick={(term) => popover.openForTerm(term)}
          onPopoverBack={() => popover.back()}
          historyDepth={popover.historyDepth}
          onSaveTranslation={handleSaveTranslation}
          onAutoTranslate={handleAutoTranslate}
          onSaveNote={handleSaveNote}
          onRefreshSection={handleRefreshSection}
          onClose={() => popover.close()}
          actionLoading={actionLoading}
          locked={popover.wordData?.locked}
          isMobile={popover.popoverPos?.isMobile}
          isAdmin={user?.is_admin}
          isExpression={popover.isExpression}
          hasCachedAudio={popover.hasCachedAudio}
          isNewEntry={popover.isNewEntry}
        />
      )}

      {/* Admin: global subtitle editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl" data-testid="admin-edit-script-dialog">
          <DialogHeader>
            <DialogTitle>Edit subtitle script (admin)</DialogTitle>
            <DialogDescription>
              Changes save globally and replace the subtitles for every user watching this video in{' '}
              <span className="font-mono uppercase">{lang}</span>. Keep the{' '}
              <span className="font-mono">[mm:ss]</span> timestamps to preserve synchronism.
            </DialogDescription>
          </DialogHeader>
          {editorLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Textarea
              value={editorText}
              onChange={(e) => setEditorText(e.target.value)}
              className="font-mono text-xs min-h-[50vh]"
              placeholder="[00:00] Line 1…\n[00:03] Line 2…"
              data-testid="admin-edit-script-textarea"
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)} data-testid="admin-edit-script-cancel">
              Cancel
            </Button>
            <Button
              onClick={saveScriptEditor}
              disabled={editorSaving || editorLoading}
              data-testid="admin-edit-script-save"
            >
              {editorSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save globally
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
